import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { gsap } from 'gsap';
import { RichTextEditor } from './editor/RichTextEditor';
import {
  X,
  Image as ImageIcon,
  Loader2,
  User,
  ChevronDown,
} from 'lucide-react';
import { useCreatePost } from '@/hooks/usePost';
import { useAuth } from '@/providers/AuthProvider';
import { processAvatarUrl } from '@/lib/mediaUtils';
import type { PostType } from '@/types';
import type { JSONContent } from '@tiptap/core';

const POST_TYPES: { value: PostType; label: string; description: string }[] = [
  { value: 'POST', label: 'Post', description: 'Share a text update' },
  { value: 'SONG', label: 'Song', description: 'Share a song' },
  { value: 'VIDEO', label: 'Video', description: 'Share a video' },
  { value: 'SHORT', label: 'Short', description: 'Share a short clip' },
];

const MAX_CHARS = 500;

interface PostComposerProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export default function PostComposer({ onClose, onSuccess }: PostComposerProps) {
  const { user } = useAuth();
  const userRecord = user as Record<string, unknown> | null;
  const displayName = (userRecord?.displayName as string) || (userRecord?.name as string) || user?.username || 'You';
  const rawAvatar = (userRecord?.avatarUrl as string) || (userRecord?.avatar as string) || '';
  const avatarUrl = processAvatarUrl(rawAvatar) || '';

  const [body, setBody] = useState(''); // Plain text for character counting
  const [editorContent, setEditorContent] = useState<JSONContent | null>(null);
  const [postType, setPostType] = useState<PostType>('POST');
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createPostMutation = useCreatePost();

  const charsLeft = MAX_CHARS - body.length;
  const isOverLimit = charsLeft < 0;
  const canSubmit = body.trim().length > 0 && !isOverLimit && !createPostMutation.isPending && !isUploading;

  // Entry animation
  useEffect(() => {
    if (overlayRef.current && sheetRef.current) {
      gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'power2.out' });
      gsap.fromTo(sheetRef.current, { y: '100%' }, { y: '0%', duration: 0.35, ease: 'power3.out' });
    }
  }, []);

  const animateClose = useCallback((cb: () => void) => {
    if (sheetRef.current && overlayRef.current) {
      gsap.to(sheetRef.current, { y: '100%', duration: 0.25, ease: 'power3.in' });
      gsap.to(overlayRef.current, {
        opacity: 0, duration: 0.25, ease: 'power2.in',
        onComplete: cb,
      });
    } else {
      cb();
    }
  }, []);

  const handleClose = useCallback(() => {
    animateClose(onClose);
  }, [animateClose, onClose]);

  // Image selection
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Submit
  const handleSubmit = async () => {
    if (!canSubmit) return;

    const userUlid = (user as Record<string, unknown>)?.ulid as string | undefined;

    try {
      let imageUrl: string | undefined;

      if (imageFile && userUlid) {
        setIsUploading(true);
        const { getSdk } = await import('@/lib/sdk');
        const sdk = getSdk();
        const result = await sdk.uploadMediaFile(imageFile, {
          userUlid,
          mediaType: 'image',
        });
        imageUrl = result.url;
        setIsUploading(false);
      }

      const payload: Record<string, unknown> = {
        body: editorContent ? JSON.stringify(editorContent) : body,
        type: postType,
      };
      if (imageUrl) payload.imageUrl = imageUrl;

      await createPostMutation.mutateAsync(payload);
      animateClose(() => {
        onClose();
        onSuccess?.();
      });
    } catch {
      setIsUploading(false);
    }
  };

  const currentType = POST_TYPES.find((t) => t.value === postType)!;

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[90] bg-black/70"
      onClick={handleClose}
    >
      <div
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 bg-[#0F1117] rounded-t-[24px] flex flex-col"
        style={{ maxHeight: '92vh', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-[#2A2F3A] rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] flex-shrink-0">
          <button
            onClick={handleClose}
            className="w-9 h-9 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8]"
          >
            <X size={18} />
          </button>

          <div className="flex items-center gap-2">
            {/* Post type selector */}
            <div className="relative">
              <button
                onClick={() => setShowTypeMenu((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#141821] rounded-full text-xs text-[#A7B0C8] font-medium"
              >
                {currentType.label}
                <ChevronDown size={13} />
              </button>
              {showTypeMenu && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-[#1A1F2E] rounded-xl border border-white/[0.08] shadow-xl z-10 overflow-hidden">
                  {POST_TYPES.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => { setPostType(t.value); setShowTypeMenu(false); }}
                      className={`w-full px-4 py-3 text-left flex flex-col gap-0.5 hover:bg-white/[0.04] transition-colors ${postType === t.value ? 'bg-[#A38BFF]/10' : ''}`}
                    >
                      <span className={`text-sm font-medium ${postType === t.value ? 'text-[#A38BFF]' : 'text-[#F5F7FF]'}`}>
                        {t.label}
                      </span>
                      <span className="text-xs text-[#A7B0C8]">{t.description}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Post button */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                canSubmit
                  ? 'bg-[#A38BFF] text-[#0B0D10] active:scale-95'
                  : 'bg-[#A38BFF]/30 text-[#A38BFF]/50 cursor-not-allowed'
              }`}
            >
              {(createPostMutation.isPending || isUploading) ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                'Post'
              )}
            </button>
          </div>
        </div>

        {/* Composer body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
          <div className="flex gap-3">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {avatarUrl ? (
                <img src={avatarUrl} alt={displayName} className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center">
                  <User size={18} className="text-[#A7B0C8]" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#F5F7FF] mb-2">{displayName}</p>

              {/* Rich Text Editor */}
              <RichTextEditor
                placeholder="What's on your mind?"
                maxLength={500}
                onUpdate={(content, text) => {
                  setEditorContent(content);
                  setBody(text);
                }}
              />

              {/* Image preview */}
              {imagePreview && (
                <div className="relative mt-3 rounded-xl overflow-hidden">
                  <img src={imagePreview} alt="Preview" className="w-full max-h-64 object-cover rounded-xl" />
                  <button
                    onClick={removeImage}
                    className="absolute top-2 right-2 w-7 h-7 bg-black/60 rounded-full flex items-center justify-center text-white"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="px-4 py-3 border-t border-white/[0.06] flex items-center flex-shrink-0">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-10 h-10 rounded-full flex items-center justify-center text-[#A7B0C8] hover:text-[#F5F7FF] hover:bg-white/[0.04] transition-colors"
          >
            <ImageIcon size={20} />
          </button>
        </div>

        {/* Error */}
        {createPostMutation.isError && (
          <div className="px-4 pb-3 text-xs text-[#FF4D4D] flex-shrink-0">
            Failed to post. Please try again.
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageSelect}
        />
      </div>
    </div>,
    document.body
  );
}
